import React, { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress,
  InputAdornment, MenuItem, Select as MuiSelect, FormControl,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LabelIcon from '@mui/icons-material/Label';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PercentIcon from '@mui/icons-material/Percent';

import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const GST_OPTIONS = [0, 1.5, 2.5, 6, 9, 14];

const Field = ({ label, name, value, onChange, placeholder, type = 'text',
  required, align, startIcon, maxLength }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small" name={name} value={value ?? ''} onChange={onChange}
      placeholder={placeholder || label} type={type} required={required}
      inputProps={{ maxLength, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      InputProps={startIcon ? { startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment> } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px', bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

const GSTField = ({ label, name, value, onChange, required }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <FormControl size="small">
      <MuiSelect
        name={name} value={value ?? 0} onChange={onChange}
        sx={{
          fontSize: '12px', borderRadius: '7px', bgcolor: '#fafafa',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.cardBorder },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.accentBorder },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accent, borderWidth: 1.5 },
          '& .MuiSelect-select': { padding: '5px 8px', fontSize: '12px' },
        }}
      >
        {GST_OPTIONS.map(v => <MenuItem key={v} value={v} sx={{ fontSize: '12px' }}>{v}</MenuItem>)}
      </MuiSelect>
    </FormControl>
  </Box>
);

const Section = ({ title, icon, children, color = C.accent }) => (
  <Box sx={{
    bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
    border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
      bgcolor: `${color}0d`,
    }}>
      <Box sx={{ color, fontSize: 15, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      {children}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const CategoryEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchData('CategoryApi', `/CategoryData/${id}`, token)
      .then(res => { if (res.responseCode === '1111') setFormData(res.response_data); })
      .catch(err => console.error('Error fetching category:', err));
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.Name?.trim())    { setError('Name is required.'); return; }
    if (!formData.HSNCode?.trim()) { setError('HSN Code is required.'); return; }
    if (parseFloat(formData.CGSTRate) !== parseFloat(formData.SGSTRate)) {
      setError('CGST and SGST rates must be equal.'); return;
    }

    setSaving(true);
    try {
      await updateData('CategoryApi', id, { ...formData, UpdatedBy: userId }, token);
      setSaved(true);
      setTimeout(() => { setSaved(false); history.push('/CategoryIndex'); }, 1200);
    } catch (err) {
      console.error('Error updating category:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <CategoryIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Category Edit
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button component={Link} to="/CategoryIndex" size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 600, textTransform: 'none',
              color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
            }}>
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={saving} size="small"
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 700, textTransform: 'none',
              bgcolor: saved ? '#22c55e' : C.accent, color: '#fff',
              borderRadius: '7px', px: 1.4, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDark },
              transition: 'background 0.3s',
            }}>
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Update'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 1, px: '10px', py: '6px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px' }}>
          <Typography sx={{ fontSize: '11px', color: C.danger }}>{error}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          <Section title="Category Information" icon={<LabelIcon />}>
            <Box sx={{ flex: '1 1 260px' }}>
              <Field label="Name" name="Name" value={formData.Name}
                onChange={handleChange} required maxLength={100} />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Field label="HSN Code" name="HSNCode" value={formData.HSNCode}
                onChange={handleChange} required maxLength={20}
                startIcon={<QrCodeIcon sx={{ fontSize: 13, color: C.muted }} />} />
            </Box>
          </Section>

          <Section title="GST Rates" icon={<PercentIcon />} color="#f59e0b">
            <Box sx={{ flex: '1 1 120px' }}>
              <GSTField label="CGST Rate" name="CGSTRate" value={formData.CGSTRate}
                onChange={handleChange} required />
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <GSTField label="SGST Rate" name="SGSTRate" value={formData.SGSTRate}
                onChange={handleChange} required />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
              <Typography sx={{ fontSize: '11px', color: C.muted }}>
                Total GST: <strong style={{ color: C.text }}>
                  {Number(formData.CGSTRate ?? 0) + Number(formData.SGSTRate ?? 0)}%
                </strong>
              </Typography>
            </Box>
          </Section>

        </Box>
      </form>
    </Box>
  );
};

export default CategoryEdit;

